import React, { useContext } from 'react';
import Scroll from 'react-scroll';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import ServicesForm from '../ServicesForm';
import Triple from '../content/Triple';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import SiteRequirements from '../content/SiteRequirements';
import Testimonials from '../content/Testimonials';
import ResidentialFaq from '../faqs/ResidentialFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';

const featuredTrucks = { featured: true };

function Residential() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div>
      <Meta
        title={`Food ${vehicle_type}s for Residential Communities`}
        breadcrumbName='Residential Services'
        type='website'
        description={`Bring food ${vehicleType}s to your apartment or HOA community in ${city_name}. Schedule rotating food ${vehicleType} visits for residents with curated weekly menus.`}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/Residential.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Food {vehicle_type}s @ Home</h1>
            <p>Schedule a daily or weekly rotation of food {vehicleType}s to visit you on site, set up and sell food directly to your apartment residents or HOA community. All at little to no cost to you!</p>
            <p>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                Start the conversation
              </Scroll.Link>
            </p>
          </div>
        </div>
      </div>

      <div className='Section'>
        <LogoList residential title='Partner with communities like' />
      </div>

      <section className='Section'>
        <h2>How It Works</h2>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services1.webp'
            isResponsive
            title='📅 Curated Schedule'
            text={`Our platform automatically schedules ${city_name}'s best food ${vehicleType}s to visit your apartment complex or HOA neighborhood on a daily rotation. By learning your residents' favorite food ${vehicleType}s, your schedule changes as it goes, with cuisines from all over the world.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services2.webp'
            isResponsive
            title='📩 Fully Automated'
            text={`Your residents and neighbors receive weekly menu emails of food ${vehicleType}s scheduled each week. A customized web page is also built for your location to view that week's food ${vehicleType} lunch lineup. Put your resident event programs on autopilot.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services3.webp'
            isResponsive
            title='✅ Hassle-Free Experience'
            text={`Food ${vehicleType}s arrive, serve and sell lunch or dinner to your residents and leave with no mess behind. Residents can order at the truck or skip the line and order online. All food is made to order from a gourmet kitchen on wheels.`}
          />
        </div>
      </section>

      <FeaturedTrucks
        title={`Meet ${city_name}'s Best Food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <section className='Section--extraPadding'>
        <h2>The Amenity Residents Rave About</h2>
        <div className={tripleStyles.Triple_wrapper + ' ' + tripleStyles.Triple_wrapper__quad}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services4.webp'
            isResponsive
            title='🏆 Attract More Residents'
            text='Residents consider on-site amenities to be one of the top factors when choosing an apartment community. Help build a sense of joint activity and community while giving your residents a one-of-a-kind work from home perk.'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services5.webp'
            isResponsive
            title='⏳ Retain Happy Residents'
            text={`The year has given many residents delivery fatigue! Food ${vehicleType}s can provide on-site options while maintaining safe distancing protocols, helping residents feel more connected to their neighbors and community.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services6.webp'
            isResponsive
            title='🤝 Drive Down Event Costs'
            text={`Community managers and HOAs can spend over 20 hours per month planning resident events. Turn your resident perks on auto-pilot, with a rotating selection of delicious cuisines from the best food ${vehicleType}s.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/services7.webp'
            isResponsive
            title='🌍 Support Local Businesses'
            text={`Our owner-operated food ${vehicleType}s favor quality, variety, and sustainability. Experience cuisines from all over the world without ever leaving your home.`}
          />
        </div>
      </section>

      <SiteRequirements />

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/Resident_Event_Credits.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>🍽️ Resident Credit Packages, Incentives, and Rewards</h3>
            <p>Offer free meals, credits and a rewards program to incentivize current and prospective residents to sign a new lease or treat those in your neighborhood association!</p>
            <div>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                Get Started!
              </Scroll.Link>
            </div>
          </div>
        </div>
      </section>

      <Testimonials title='What People Are Saying' />

      <section className='Section--extraPadding'>
        <ResidentialFaq />
      </section>

      <Scroll.Element name='form'>
        <div className='GrayBg'>
          <section className='Section--extraPadding'>
            <h2>Let Us Help With Your Community Events</h2>
            <ServicesForm formType='residential' />
          </section>
        </div>
      </Scroll.Element>
    </div>
  );
}

export default Residential;
